import type { CSSObject } from '@mui/material/styles';

import Box from '@mui/material/Box';
import Badge from '@mui/material/Badge';
import TableCell from '@mui/material/TableCell';
import IconButton from '@mui/material/IconButton';
import TableSortLabel from '@mui/material/TableSortLabel';

import { Iconify } from '../../../iconify';

import type { TableHeadCellProps } from './table-head-custom';

// ----------------------------------------------------------------------

const visuallyHidden: CSSObject = {
  border: 0,
  padding: 0,
  width: '1px',
  height: '1px',
  margin: '-1px',
  overflow: 'hidden',
  position: 'absolute',
  whiteSpace: 'nowrap',
  clip: 'rect(0 0 0 0)',
};

// ----------------------------------------------------------------------

type HeaderCellProps = {
  headCell: TableHeadCellProps;
  order?: 'asc' | 'desc';
  orderBy?: string;
  onSort?: (id: string) => void;
  sortFldList?: string[];
  isFilterApplied: boolean;
  onFilterClick: (event: React.MouseEvent<HTMLElement>, column: TableHeadCellProps) => void;
};

// ----------------------------------------------------------------------

export function HeaderCell({
  headCell,
  order,
  orderBy,
  onSort,
  sortFldList,
  isFilterApplied,
  onFilterClick,
}: HeaderCellProps) {
  return (
    <TableCell
      key={headCell.id}
      align={headCell.id === 'redirect' ? 'center' : headCell.align || 'left'}
      sortDirection={orderBy === headCell.id ? order : false}
      sx={{
        width: headCell.width,
        textAlign: headCell.id === 'redirect' ? 'center' : undefined,
        ...headCell.sx,
      }}
    >
      <Box
        display="flex"
        alignItems="center"
        justifyContent={
          headCell.id === 'redirect' || headCell.align === 'center'
            ? 'center'
            : headCell.align === 'right'
              ? 'flex-end'
              : 'flex-start'
        }
      >
        {(!sortFldList || sortFldList.length === 0 || sortFldList.includes(headCell.id)) &&
        onSort ? (
          <TableSortLabel
            hideSortIcon
            active={orderBy === headCell.id}
            direction={orderBy === headCell.id ? order : 'asc'}
            onClick={() => onSort(headCell.id)}
          >
            {headCell.label}
            {orderBy === headCell.id && (
              <Box component="span" sx={visuallyHidden}>
                {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
              </Box>
            )}
          </TableSortLabel>
        ) : (
          headCell.label
        )}

        {headCell.filterable && (
          <Badge
            color="error"
            variant="dot"
            overlap="circular"
            anchorOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            invisible={!isFilterApplied}
          >
            <IconButton
              size="small"
              sx={{ mx: 2 }}
              onClick={(event) => onFilterClick(event, headCell)}
            >
              <Iconify icon="mdi:filter" />
            </IconButton>
          </Badge>
        )}
      </Box>
    </TableCell>
  );
}
